import React, { useEffect, useState } from "react";
import { ArrowRight, Trophy } from "lucide-react";
import { TIER_VISUALS } from "./VipMembershipCard";

export const VipProgressBar = ({ progress }) => {
    // The backend returns: { current, next (tier obj or null), remaining_to_next, lifetime_spend }
    const current_spend = Number(progress?.lifetime_spend || 0);
    const next = progress?.next || null;
    const next_tier = next?.slug || null;
    const next_tier_label = next?.label || "";
    const next_tier_threshold = Number(next?.threshold || 0);
    const remaining = Number(progress?.remaining_to_next || 0);
    const is_max = !next;
    // Percentage between previous threshold and next threshold so the bar feels
    // earned rather than always near-empty.
    const prev_threshold = Number(progress?.current?.threshold || 0);
    let pct_to_next = 0;
    if (!is_max && next_tier_threshold > prev_threshold) {
        pct_to_next = Math.min(
            100,
            Math.max(0, ((current_spend - prev_threshold) / (next_tier_threshold - prev_threshold)) * 100)
        );
    }

    // Animate the bar fill on mount / when progress updates
    const [animatedPct, setAnimatedPct] = useState(0);
    useEffect(() => {
        const target = is_max ? 100 : pct_to_next;
        const t = setTimeout(() => setAnimatedPct(target), 80);
        return () => clearTimeout(t);
    }, [pct_to_next, is_max]);

    if (is_max) {
        return (
            <div
                className="vip-max-card rounded-2xl p-5 sm:p-6 text-center"
                style={{
                    background: TIER_VISUALS.diamond.bg,
                    border: `1px solid ${TIER_VISUALS.diamond.border}`,
                    boxShadow: TIER_VISUALS.diamond.glow,
                }}
                data-testid="vip-max-level"
            >
                <Trophy size={32} className="mx-auto mb-2" style={{ color: TIER_VISUALS.diamond.accent }} />
                <p className="text-[10px] font-mono uppercase tracking-[0.4em] text-white/60 mb-1">
                    Max level achieved
                </p>
                <p className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight">
                    💠 Diamond
                </p>
                <p className="text-xs sm:text-sm text-white/60 mt-1">
                    You&apos;re at the top of the Carbon Labs VIP system.
                </p>
            </div>
        );
    }

    const nextTierVisual = TIER_VISUALS[next_tier] || TIER_VISUALS.bronze;
    return (
        <div
            className="rounded-2xl p-5 sm:p-6 glass-elevated"
            data-testid="vip-progress-bar"
        >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
                        Lifetime spend
                    </p>
                    <p className="font-display text-2xl sm:text-3xl font-black text-white tabular-nums" data-testid="vip-progress-spend">
                        A${Number(current_spend || 0).toFixed(2)}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
                        Next tier
                    </p>
                    <p className="font-display text-lg sm:text-xl font-bold" style={{ color: nextTierVisual.accent }}>
                        {next_tier_label || nextTierVisual.label}
                    </p>
                </div>
            </div>

            <div className="relative h-3 bg-white/5 rounded-full overflow-hidden mb-3">
                <div
                    className="h-full rounded-full vip-progress-fill"
                    style={{
                        width: `${animatedPct}%`,
                        background: `linear-gradient(90deg, ${nextTierVisual.accentSoft}, ${nextTierVisual.accent})`,
                        boxShadow: `0 0 16px ${nextTierVisual.accentSoft}`,
                        transition: "width 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                    data-testid="vip-progress-fill"
                />
                <div className="absolute inset-0 rounded-full" style={{ boxShadow: "inset 0 0 6px rgba(0,0,0,0.6)" }} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm">
                <p className="text-[var(--text-secondary)]">
                    Only{" "}
                    <span className="font-mono font-bold text-white" data-testid="vip-progress-remaining">
                        A${Number(remaining || 0).toFixed(2)}
                    </span>{" "}
                    remaining
                </p>
                <p className="font-mono text-[var(--text-muted)] flex items-center gap-1">
                    <span className="font-bold" style={{ color: nextTierVisual.accent }} data-testid="vip-progress-pct">
                        {Math.round(animatedPct)}%
                    </span>
                    <span>complete</span>
                    <ArrowRight size={11} className="ml-1" />
                    <span>A${Number(next_tier_threshold || 0).toFixed(0)}</span>
                </p>
            </div>
        </div>
    );
};
